import React, { useState, useMemo } from 'react';
import { BarChart3, AlertTriangle, TrendingUp, HelpCircle, ShoppingCart, Info } from 'lucide-react';

/* ── Tab config ──────────────────────────────────────────── */
const TABS = [
  { key: 'by_price_range', label: 'Prix' },
  { key: 'by_fuel', label: 'Carburant' },
  { key: 'by_body_type', label: 'Catégorie' },
  { key: 'by_make', label: 'Marque' },
  { key: 'by_year', label: 'Année' },
  { key: 'by_model', label: 'Modèle' },
];

/* ── Price range buckets (ordered) ────────────────────────── */
const PRICE_RANGES = [
  { key: '0-5000', label: '0 - 5k €', min: 0, max: 5000 },
  { key: '5000-10000', label: '5k - 10k €', min: 5000, max: 10000 },
  { key: '10000-20000', label: '10k - 20k €', min: 10000, max: 20000 },
  { key: '20000-35000', label: '20k - 35k €', min: 20000, max: 35000 },
  { key: '35000+', label: '35k+ €', min: 35000, max: Infinity },
];

const ANALYSIS_PERIOD = 90; // days of sales history

/* ── Helpers ──────────────────────────────────────────────── */
const getPriceRange = (price) => {
  for (const r of PRICE_RANGES) {
    if (price >= r.min && price < r.max) return r.key;
  }
  return '35000+';
};

const formatLabel = (tab, raw) => {
  if (tab === 'by_price_range') {
    const found = PRICE_RANGES.find(r => r.key === raw);
    return found ? found.label : raw;
  }
  return raw;
};

/* ── Info Tooltip ─────────────────────────────────────────── */
const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-flex">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="text-muted hover:text-secondary transition-colors"
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </button>
      {show && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-72 p-3
          bg-dark-surface border border-dark-border rounded-lg shadow-lg z-50">
          <p className="text-xs text-secondary leading-relaxed whitespace-pre-line">{text}</p>
        </div>
      )}
    </div>
  );
};

/* ── Dual Bar Row ─────────────────────────────────────────── */
const DualBarRow = ({ label, stockCount, idealCount, soldCount, avgDays, maxVal, hasImbalance }) => {
  const stockPct = maxVal > 0 ? (stockCount / maxVal) * 100 : 0;
  const idealPct = maxVal > 0 ? (idealCount / maxVal) * 100 : 0;

  const hasTooltip = avgDays != null || soldCount > 0;
  const tooltipParts = [];
  if (avgDays != null) tooltipParts.push(`Vendu en moy. ${avgDays}j`);
  if (soldCount > 0) tooltipParts.push(`${soldCount} vente${soldCount > 1 ? 's' : ''} sur ${ANALYSIS_PERIOD}j`);
  const rowTooltip = tooltipParts.join(' · ');

  return (
    <div className="flex items-center gap-3 group/row relative rounded-lg px-2 py-1.5 -mx-2 transition-colors hover:bg-white/[0.03]">
      <span className="text-xs text-secondary w-20 flex-shrink-0 text-right truncate" title={label}>
        {label}
      </span>
      <div className="flex-1 flex flex-col gap-0.5">
        {/* Distribution actuelle */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-[14px] bg-dark-surface rounded overflow-hidden">
            <div
              className="h-full rounded transition-all duration-500"
              style={{ width: `${Math.max(stockPct, stockCount > 0 ? 3 : 0)}%`, background: 'rgba(6,182,212,0.4)' }}
            />
          </div>
          <span className="text-xs font-medium text-primary w-5 text-right">{stockCount}</span>
        </div>
        {/* Distribution idéale */}
        {idealCount > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-[14px] bg-dark-surface rounded overflow-hidden">
              <div
                className="h-full rounded transition-all duration-500"
                style={{ width: `${Math.max(idealPct, 3)}%`, background: 'rgba(168,85,247,0.2)' }}
              />
            </div>
            <span className="text-xs font-medium text-muted w-5 text-right">{idealCount}</span>
          </div>
        )}
      </div>
      {/* Tooltip below row on hover */}
      {hasTooltip && (
        <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-3 py-1.5 text-xs text-cyan-400 bg-dark-surface border border-dark-border rounded-lg shadow-lg whitespace-nowrap opacity-0 group-hover/row:opacity-100 transition-opacity pointer-events-none z-50">
          {rowTooltip}
        </span>
      )}
      {/* Imbalance icon */}
      <span className="w-5 flex-shrink-0 flex justify-center">
        {hasImbalance === 'under' && (
          <span className="group relative">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
            <span className="absolute bottom-full right-0 mb-1.5 px-2 py-1 text-[10px] text-secondary bg-dark-surface border border-dark-border rounded-md shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              Sous-stocké — demande supérieure au stock
            </span>
          </span>
        )}
        {hasImbalance === 'over' && (
          <span className="group relative">
            <TrendingUp className="h-3.5 w-3.5 text-orange-400" />
            <span className="absolute bottom-full right-0 mb-1.5 px-2 py-1 text-[10px] text-secondary bg-dark-surface border border-dark-border rounded-md shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              Sur-stocké — stock supérieur à la demande
            </span>
          </span>
        )}
      </span>
    </div>
  );
};

/* ── Main Component ──────────────────────────────────────── */
const StockAnalytics = ({ vehicles, sellDuration }) => {
  const [tab, setTab] = useState('by_price_range');

  // Compute stock distribution from current vehicles
  const stockDistribution = useMemo(() => {
    if (!vehicles) return {};
    const inStock = vehicles.filter(
      v => v.stock_status === 'available' || v.stock_status === 'reserved' || v.stock_status === 'in_preparation'
    );

    const dist = {
      by_price_range: {},
      by_fuel: {},
      by_body_type: {},
      by_make: {},
      by_year: {},
      by_model: {},
    };

    for (const v of inStock) {
      const pr = getPriceRange(v.price || 0);
      dist.by_price_range[pr] = (dist.by_price_range[pr] || 0) + 1;

      const fuel = v.fuel_type || 'Autre';
      dist.by_fuel[fuel] = (dist.by_fuel[fuel] || 0) + 1;

      const bodyType = v.body_type || 'Autre';
      dist.by_body_type[bodyType] = (dist.by_body_type[bodyType] || 0) + 1;

      const make = v.make || 'Autre';
      dist.by_make[make] = (dist.by_make[make] || 0) + 1;

      const year = String(v.year || 'Autre');
      dist.by_year[year] = (dist.by_year[year] || 0) + 1;

      const model = `${make} ${v.model || 'Autre'}`;
      dist.by_model[model] = (dist.by_model[model] || 0) + 1;
    }

    // Count vehicles with missing info per field
    const missingFields = {
      by_body_type: inStock.filter(v => !v.body_type).length,
      by_fuel: inStock.filter(v => !v.fuel_type).length,
      by_year: inStock.filter(v => !v.year).length,
    };

    return { dist, totalStock: inStock.length, missingFields };
  }, [vehicles]);

  // Build merged rows for current tab
  const computed = useMemo(() => {
    const soldItems = sellDuration?.[tab] || [];
    const stockDist = stockDistribution.dist?.[tab] || {};
    const total = stockDistribution.totalStock || 0;

    // Collect all labels (union of stock + sold)
    let allLabels;
    if (tab === 'by_price_range') {
      allLabels = PRICE_RANGES.map(r => r.key);
    } else {
      const labelSet = new Set([...Object.keys(stockDist), ...soldItems.map(i => i.label)]);
      if (tab === 'by_year') {
        allLabels = [...labelSet].sort((a, b) => b.localeCompare(a));
      } else {
        allLabels = [...labelSet].sort((a, b) => {
          const aTotal = (stockDist[a] || 0) + (soldItems.find(i => i.label === a)?.count || 0);
          const bTotal = (stockDist[b] || 0) + (soldItems.find(i => i.label === b)?.count || 0);
          return bTotal - aTotal;
        });
      }
    }

    const soldMap = {};
    for (const item of soldItems) {
      soldMap[item.label] = item;
    }

    // --- Pass 1: compute weights for proportional allocation ---
    let tSold = 0;
    let totalWeight = 0;
    const rawRows = allLabels.map(label => {
      const stockCount = stockDist[label] || 0;
      const soldData = soldMap[label];
      const soldCount = soldData?.count || 0;
      const avgDays = soldData?.avg_days ?? null;
      tSold += soldCount;

      // Weight = sales volume × avg rotation time (higher = needs more stock)
      const weight = soldCount > 0 && avgDays != null
        ? soldCount * avgDays
        : 0;
      totalWeight += weight;

      return { label, stockCount, soldCount, avgDays, weight, rawLabel: label };
    });

    // --- Pass 2: distribute total stock proportionally (largest remainder method) ---
    // Guarantees totalIdeal === totalStock regardless of tab/rounding
    const rawAllocations = rawRows.map(row =>
      totalWeight > 0 && row.weight > 0 ? total * (row.weight / totalWeight) : 0
    );
    const floored = rawAllocations.map(v => Math.floor(v));
    let remaining = total - floored.reduce((a, b) => a + b, 0);
    // Distribute remaining units to segments with highest fractional parts
    rawAllocations
      .map((v, i) => ({ i, r: v - floored[i] }))
      .filter(x => x.r > 0)
      .sort((a, b) => b.r - a.r)
      .forEach(({ i }) => { if (remaining > 0) { floored[i]++; remaining--; } });

    let tIdeal = 0;
    const merged = rawRows.map((row, idx) => {
      const isAutre = row.label === 'Autre';
      const idealCount = isAutre ? 0 : floored[idx];
      tIdeal += idealCount;

      // Imbalance: skip for "Autre" (incomplete data)
      let hasImbalance = null;
      if (!isAutre) {
        if (row.soldCount > 0 && row.stockCount === 0) {
          hasImbalance = 'under';
        } else if (idealCount > 0 && row.stockCount < idealCount * 0.5) {
          hasImbalance = 'under';
        } else if (idealCount > 0 && row.stockCount > idealCount * 2 && row.stockCount >= 3) {
          hasImbalance = 'over';
        } else if (row.soldCount === 0 && row.stockCount >= 4) {
          hasImbalance = 'over';
        }
      }

      return {
        label: formatLabel(tab, row.rawLabel),
        stockCount: row.stockCount,
        idealCount,
        soldCount: row.soldCount,
        avgDays: row.avgDays,
        hasImbalance,
        rawLabel: row.rawLabel,
      };
    });

    // Filter out rows with 0 in both stock and sold (except price ranges)
    const rows = tab === 'by_price_range'
      ? merged
      : merged.filter(r => r.stockCount > 0 || r.soldCount > 0);

    // For model tab: group by make, show model name only
    if (tab === 'by_model') {
      rows.sort((a, b) => {
        const makeA = a.rawLabel.split(' ')[0];
        const makeB = b.rawLabel.split(' ')[0];
        if (makeA !== makeB) return makeA.localeCompare(makeB);
        return (b.stockCount + b.soldCount) - (a.stockCount + a.soldCount);
      });
      let lastMake = '';
      for (const row of rows) {
        const make = row.rawLabel.split(' ')[0];
        if (make !== lastMake) {
          row.makeGroup = make;
          lastMake = make;
        }
        // Show just the model name under the group header
        row.label = row.rawLabel.substring(make.length + 1) || row.rawLabel;
      }
    }

    // Auto-insights
    const opportunities = rows.filter(r => r.soldCount > 0 && r.stockCount === 0);
    const underStocked = rows.filter(r => r.hasImbalance === 'under' && r.stockCount > 0);
    const overStocked = rows.filter(r => r.hasImbalance === 'over');
    const insights = [];
    if (opportunities.length > 0) {
      const details = opportunities.slice(0, 4).map(r =>
        `${r.label} (${r.soldCount} ventes en ${ANALYSIS_PERIOD}j, 0 en stock)`
      ).join(' · ');
      insights.push({ type: 'opportunity', text: `Opportunité d'achat : ${details}` });
    }
    if (underStocked.length > 0) {
      const details = underStocked.slice(0, 4).map(r =>
        `${r.label} (${r.stockCount} en stock, idéal ${r.idealCount})`
      ).join(' · ');
      insights.push({ type: 'under', text: `Sous-stocké : ${details}` });
    }
    if (overStocked.length > 0) {
      const details = overStocked.slice(0, 4).map(r => {
        if (r.soldCount === 0) return `${r.label} (${r.stockCount} en stock, aucune vente)`;
        return `${r.label} (${r.stockCount} en stock, idéal ${r.idealCount})`;
      }).join(' · ');
      insights.push({ type: 'over', text: `Sur-stocké : ${details}` });
    }

    const missingCount = stockDistribution.missingFields?.[tab] || 0;

    return { rows, totalStock: total, totalIdeal: tIdeal, totalSold: tSold, insights, missingCount };
  }, [tab, sellDuration, stockDistribution]);

  const { rows, totalStock, totalIdeal, totalSold, insights, missingCount } = computed;

  // Max value for bar scaling
  const maxVal = Math.max(...rows.flatMap(r => [r.stockCount, r.idealCount]), 1);

  if (!vehicles || vehicles.length === 0) return null;

  return (
    <div className="bg-dark-card rounded-xl border border-dark-border p-4 sm:p-5 space-y-4">
      {/* Header */}
      <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-cyan-500" />
        Analyse du stock
        <InfoTooltip text={`La distribution idéale montre comment répartir votre stock en fonction de la demande réelle des ${ANALYSIS_PERIOD} derniers jours.\n\nChaque segment reçoit un poids basé sur le volume de ventes et la durée moyenne avant vente.\n\nLes écarts entre les deux barres révèlent les déséquilibres : segments sous-représentés (opportunités d'achat) ou sur-représentés dans votre stock.`} />
      </h2>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto scrollbar-hide">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
              tab === t.key
                ? 'bg-cyan-500/15 text-cyan-400'
                : 'text-muted hover:text-secondary'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3.5 h-3 rounded-sm" style={{ background: 'rgba(6,182,212,0.4)' }} />
          <span className="text-xs text-secondary">Distribution actuelle</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3.5 h-3 rounded-sm" style={{ background: 'rgba(168,85,247,0.4)' }} />
          <span className="text-xs text-secondary">Distribution idéale</span>
        </div>
      </div>

      {/* Bars */}
      <div className="space-y-3">
        {rows.map((row, i) => (
          <React.Fragment key={row.rawLabel}>
            {row.makeGroup && (
              <div className={`text-[11px] font-semibold text-secondary uppercase tracking-wide ${i > 0 ? 'mt-3 pt-3 border-t border-dark-border' : ''}`}>
                {row.makeGroup}
              </div>
            )}
            <DualBarRow
              label={row.label}
              stockCount={row.stockCount}
              idealCount={row.idealCount}
              soldCount={row.soldCount}
              avgDays={row.avgDays}
              maxVal={maxVal}
              hasImbalance={row.hasImbalance}
            />
          </React.Fragment>
        ))}
        {rows.length === 0 && (
          <p className="text-xs text-muted text-center py-2">Pas encore de données</p>
        )}
      </div>

      {/* Missing field warning */}
      {missingCount > 0 && (
        <div className="rounded-lg p-3 flex items-start gap-2.5"
          style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)' }}
        >
          <Info className="h-4 w-4 text-indigo-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-indigo-400">
            <span className="font-semibold">{missingCount} véhicule{missingCount > 1 ? 's' : ''}</span> en stock {missingCount > 1 ? 'n\'ont' : 'n\'a'} pas de {
              tab === 'by_body_type' ? 'catégorie' : tab === 'by_fuel' ? 'type de carburant' : 'année'
            } renseigné{tab === 'by_body_type' ? 'e' : ''} et {missingCount > 1 ? 'apparaissent' : 'apparaît'} dans « Autre ». Complétez les fiches pour une analyse plus précise.
          </p>
        </div>
      )}

      {/* Auto-insights */}
      {insights.length > 0 && (
        <div className="space-y-2">
          {insights.map((ins, i) => {
            const styles = {
              opportunity: { bg: 'rgba(34,197,94,0.06)', border: 'rgba(34,197,94,0.18)', color: 'text-emerald-400' },
              under: { bg: 'rgba(234,179,8,0.06)', border: 'rgba(234,179,8,0.15)', color: 'text-amber-400' },
              over: { bg: 'rgba(249,115,22,0.06)', border: 'rgba(249,115,22,0.15)', color: 'text-orange-400' },
            }[ins.type];
            return (
              <div key={i} className="rounded-lg p-3 flex items-start gap-2.5"
                style={{ background: styles.bg, border: `1px solid ${styles.border}` }}
              >
                {ins.type === 'opportunity' && <ShoppingCart className={`h-4 w-4 ${styles.color} flex-shrink-0 mt-0.5`} />}
                {ins.type === 'under' && <AlertTriangle className={`h-4 w-4 ${styles.color} flex-shrink-0 mt-0.5`} />}
                {ins.type === 'over' && <TrendingUp className={`h-4 w-4 ${styles.color} flex-shrink-0 mt-0.5`} />}
                <p className={`text-xs font-medium ${styles.color}`}>
                  {ins.text}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StockAnalytics;
